import { styled } from '@mui/material/styles';
import FileGridItem from './FileGridItem';
import { ComplianceDocument, MarketingAssetSchema } from '@/lib/types';
import CircularProgress from '@mui/material/CircularProgress';
import { FormattedMessage } from 'react-intl';

const EMPTY_MESSAGE_BY_TYPE: Record<
  'compliance' | 'marketing' | 'game',
  { id: string; defaultMessage: string }
> = {
  compliance: {
    id: 'noComplianceDocuments',
    defaultMessage: 'No compliance documents have been added yet.',
  },
  marketing: {
    id: 'noMarketingAssets',
    defaultMessage: 'No marketing assets have been added yet.',
  },
  game: {
    id: 'noFiles',
    defaultMessage: 'No files have been added yet.',
  },
};

const FileGrid = ({
  loading,
  files,
  selected,
  onSelect,
  type = 'compliance',
  selectionMode = 'checkbox',
  onFileOpen,
  onFileDownload,
  downloading,
  exactMatchQuery,
}: {
  loading?: boolean;
  files: (ComplianceDocument | MarketingAssetSchema)[];
  selected?: number[];
  onSelect?: (selected: number[]) => void;
  type?: 'compliance' | 'marketing' | 'game';
  selectionMode?: 'checkbox' | 'radio';
  /** Called when a file is acted on (opened or downloaded). */
  onFileOpen?: (
    file: ComplianceDocument | MarketingAssetSchema,
    index: number,
    source: 'download' | 'preview',
  ) => void;
  onFileDownload?: (
    file: ComplianceDocument | MarketingAssetSchema,
  ) => void;
  downloading?: boolean;
  exactMatchQuery?: string;
}) => {
  const handleOnChange = (id: number) => {
    if (selectionMode === 'radio') {
      onSelect?.([id]);
      return;
    }
    const newSelected = selected?.includes(id)
      ? selected?.filter((s) => s !== id)
      : [...(selected || []), id];
    onSelect?.(newSelected);
  };

  if (!loading && files.length === 0) {
    const emptyMessage = EMPTY_MESSAGE_BY_TYPE[type];
    return (
      <EmptyContainer>
        <FormattedMessage
          id={emptyMessage.id}
          defaultMessage={emptyMessage.defaultMessage}
        />
      </EmptyContainer>
    );
  }

  return (
    <Root>
      {files.map((f, index) => (
        <FileGridItem
          key={f.id}
          file={f as ComplianceDocument | MarketingAssetSchema}
          selected={selected?.includes(f.id) || false}
          onChange={handleOnChange}
          type={type}
          selectionMode={selectionMode}
          onOpen={
            onFileOpen
              ? (source) => onFileOpen(f, index, source)
              : undefined
          }
          onDownload={
            onFileDownload ? () => onFileDownload(f) : undefined
          }
          downloading={downloading}
          exactMatchQuery={exactMatchQuery}
        />
      ))}
      {loading && (
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      )}
    </Root>
  );
};

const Root = styled('div')(() => ({
  display: 'flex',
  gap: 25,
  flexWrap: 'wrap',
  alignItems: 'stretch',
}));

const LoaderContainer = styled('div')(() => ({
  width: '100%',
  padding: '60px',
  display: 'flex',
  justifyContent: 'center',
}));

const EmptyContainer = styled('div')(({ theme }) => ({
  width: '100%',
  padding: '60px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  color: theme.palette.grey500,
  fontSize: 14,
  fontWeight: 500,
  lineHeight: '24px',
}));

export default FileGrid;
